import React, { useState } from "react";
import { FilterRow } from "../dashboard/FilterRow";
import { RawWeatherEvent, RouteDetails } from "../map/types/type";

interface EventGroup {
  groupKey: string;
  date: string;
  eventType: string;
  events: RawWeatherEvent[];
  parent: RawWeatherEvent;
  count: number;
}

interface WeatherEventsTableProps {
  route: RouteDetails;
  filterGroupKey?: string | null;
  clearFilter?: () => void;
  onGroupFilterChange?: (groupKey: string | null) => void;
}

const EventTable: React.FC<WeatherEventsTableProps> = ({
  route,
  filterGroupKey,
  clearFilter,
  onGroupFilterChange,
}) => {
  const events = route.events;

  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: keyof RawWeatherEvent | "eventCount" | null;
    direction: "asc" | "desc";
  }>({ key: null, direction: "asc" });

  // Filter state
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  // Temporary filter state (only applied when "Apply Filters" is clicked)
  const [appliedFilters, setAppliedFilters] = useState<{
    eventType: string | null;
    startDate: string | null;
    endDate: string | null;
  }>({ eventType: null, startDate: null, endDate: null });

  const handleExportCSV = () => {
    const csvContent = [
      ["Event Type", "CZ", "State", "Date", "Distance", "# of Events"],
      ...filteredGroups.map((group) => [
        group.parent.EVENT_TYPE,
        group.parent.CZ_NAME,
        group.parent.STATE,
        group.parent.date.split("T")[0],
        typeof group.parent.distance === "number"
          ? group.parent.distance.toFixed(2)
          : group.parent.distance,
        group.count.toString(),
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    const fileName =
      `weather_events_${route.route.name?.replace(/\s+/g, "_") || "route"}.csv`;

    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Update sort config when a header is clicked
  const handleSort = (key: keyof RawWeatherEvent | "eventCount") => {
    setSortConfig((prev) => {
      const isSameColumn = prev.key === key;
      return {
        key,
        direction: isSameColumn && prev.direction === "asc" ? "desc" : "asc",
      };
    });
  };

  const compareValues = (
    a: string | number,
    b: string | number,
    direction: "asc" | "desc"
  ) => {
    if (typeof a === "number" && typeof b === "number") {
      return direction === "asc" ? a - b : b - a;
    }
    return direction === "asc"
      ? String(a).localeCompare(String(b))
      : String(b).localeCompare(String(a));
  };

  // Group events by composite key (EVENT_TYPE and date)
  const groupedEvents = events.reduce<
    Record<
      string,
      { date: string; eventType: string; events: RawWeatherEvent[] }
    >
  >((acc, event) => {
    const key = `${event.EVENT_TYPE}-${event.date}`;
    if (!acc[key]) {
      acc[key] = { date: event.date, eventType: event.EVENT_TYPE, events: [] };
    }
    acc[key].events.push(event);
    return acc;
  }, {});

  const groups = Object.entries(groupedEvents).map(([groupKey, groupData]) => {
    const sortedGroup = [...groupData.events].sort(
      (a, b) => a.distance - b.distance
    );
    return {
      groupKey,
      date: groupData.date,
      eventType: groupData.eventType,
      events: sortedGroup,
      parent: sortedGroup[0],
      count: sortedGroup.length,
    };
  });

  let sortedGroups = [...groups];
  if (sortConfig.key) {
    if (sortConfig.key === "eventCount") {
      sortedGroups.sort((a, b) =>
        compareValues(a.count, b.count, sortConfig.direction)
      );
    } else if (sortConfig.key === "date") {
      sortedGroups.sort((a, b) =>
        sortConfig.direction === "asc"
          ? a.date.localeCompare(b.date)
          : b.date.localeCompare(a.date)
      );
    } else if (sortConfig.key === "distance") {
      sortedGroups.sort((a, b) =>
        compareValues(
          a.parent.distance,
          b.parent.distance,
          sortConfig.direction
        )
      );
    } else {
      const key = sortConfig.key as keyof RawWeatherEvent;
      sortedGroups.sort((a, b) =>
        compareValues(a.parent[key], b.parent[key], sortConfig.direction)
      );
    }
  } else {
    sortedGroups.sort((a, b) => {
      const cmp = a.date.localeCompare(b.date);
      return cmp === 0 ? a.eventType.localeCompare(b.eventType) : cmp;
    });
  }

  // Apply filters to sortedGroups
  const filteredGroups = sortedGroups.filter((group) => {
    const matchesEventType = appliedFilters.eventType
      ? group.eventType === appliedFilters.eventType
      : true;

    // Convert dates to Date objects for comparison
    const groupDate = new Date(group.date);
    const startDate = appliedFilters.startDate ? new Date(appliedFilters.startDate) : null;
    const endDate = appliedFilters.endDate
      ? (() => {
        const date = new Date(appliedFilters.endDate); // Create a Date object
        date.setDate(date.getDate() + 1); // Add one day
        return date;
      })()
      : null;

    const matchesDateRange =
      startDate && endDate
        ? groupDate >= startDate && groupDate < endDate // Use `<` instead of `<=` to include the end date
        : true;

    return matchesEventType && matchesDateRange;
  });

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (groupKey: string) => {
    const newSet = new Set(expandedGroups);
    if (newSet.has(groupKey)) {
      newSet.delete(groupKey);
      setExpandedGroups(newSet);
      // If the currently filtered group is being closed, clear the filter.
      if (filterGroupKey === groupKey && onGroupFilterChange) {
        onGroupFilterChange(null);
      }
    } else {
      newSet.add(groupKey);
      setExpandedGroups(newSet);
      // When a group is expanded, call onGroupFilterChange to filter the map.
      if (onGroupFilterChange) {
        onGroupFilterChange(groupKey);
      }
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      eventType: selectedEventType,
      startDate,
      endDate,
    });
    setShowFilterModal(false);
  };

  const handleClearFilters = () => {
    setSelectedEventType(null);
    setStartDate(null);
    setEndDate(null);
    setAppliedFilters({ eventType: null, startDate: null, endDate: null });
  };

  return (
    <div className="mx-auto max-w-7xl p-6 bg-white rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <FilterRow excludeRoute details={true} />
        <div className="flex gap-2">
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200"
          >
            Export
          </button>
        </div>
      </div>
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Filter Options</h3>
              <button
                onClick={() => setShowFilterModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Event Type</label>
                <select
                  value={selectedEventType || ""}
                  onChange={(e) => setSelectedEventType(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select Event Type</option>
                  {/* Populate with event types */}
                  <option value="Type1">Type1</option>
                  <option value="Type2">Type2</option>
                  {/* Add more options as needed */}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date Range</label>
                <div className="flex space-x-4">
                  <input
                    type="date"
                    value={startDate || ""}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  />
                  <input
                    type="date"
                    value={endDate || ""}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-500 text-white rounded"
              >
                Clear Filters
              </button>
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
      {filterGroupKey && (
        <div className="mb-4">
          <button
            onClick={clearFilter}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            Clear Filter
          </button>
        </div>
      )}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="w-full border-collapse table-fixed text-sm">
          <thead className="bg-blue-600 text-white">
            <tr>
              <th className="w-6 p-3 text-left"></th>
              <th
                className="p-3 text-left font-semibold cursor-pointer"
                onClick={() => handleSort("EVENT_TYPE")}
              >
                Event Type{" "}
                {sortConfig.key === "EVENT_TYPE" &&
                  (sortConfig.direction === "asc" ? "▲" : "▼")}
              </th>
              <th
                className="p-3 text-left font-semibold cursor-pointer"
                onClick={() => handleSort("CZ_NAME")}
              >
                CZ (County/Zone){" "}
                {sortConfig.key === "CZ_NAME" &&
                  (sortConfig.direction === "asc" ? "▲" : "▼")}
              </th>
              <th
                className="p-3 text-left font-semibold cursor-pointer"
                onClick={() => handleSort("STATE")}
              >
                State{" "}
                {sortConfig.key === "STATE" &&
                  (sortConfig.direction === "asc" ? "▲" : "▼")}
              </th>
              <th
                className="p-3 text-left font-semibold cursor-pointer"
                onClick={() => handleSort("date")}
              >
                Date{" "}
                {sortConfig.key === "date" &&
                  (sortConfig.direction === "asc" ? "▲" : "▼")}
              </th>
              <th
                className="p-3 text-left font-semibold cursor-pointer"
                onClick={() => handleSort("distance")}
              >
                Distance{" "}
                {sortConfig.key === "distance" &&
                  (sortConfig.direction === "asc" ? "▲" : "▼")}
              </th>
              <th
                className="p-3 text-left font-semibold cursor-pointer"
                onClick={() => handleSort("eventCount")}
              >
                # of Events{" "}
                {sortConfig.key === "eventCount" &&
                  (sortConfig.direction === "asc" ? "▲" : "▼")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredGroups.map((group) => {
              const isExpanded = expandedGroups.has(group.groupKey);
              const otherEvents = group.events.slice(1);
              return (
                <React.Fragment key={group.groupKey}>
                  <tr
                    className={`${isExpanded ? "bg-gray-50" : "bg-white"
                      } hover:bg-gray-100 transition-colors duration-200 cursor-pointer`}
                    onClick={() => toggleGroup(group.groupKey)}
                  >
                    <td className="text-center p-3">
                      {isExpanded ? "▼" : "►"}
                    </td>
                    <td className="p-3">{group.parent.EVENT_TYPE}</td>
                    <td className="p-3">{group.parent.CZ_NAME}</td>
                    <td className="p-3">{group.parent.STATE}</td>
                    <td className="p-3">{group.parent.date.split("T")[0]}</td>
                    <td className="p-3">
                      {typeof group.parent.distance === "number"
                        ? group.parent.distance.toFixed(2)
                        : group.parent.distance}
                    </td>
                    <td className="p-3">{group.count}</td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={7} className="bg-gray-50 px-6 py-4">
                        <div className="max-h-96 overflow-y-auto space-y-6">
                          <div className="p-4 bg-white border border-gray-200 rounded shadow-sm">
                            <div className="mb-2">
                              <span className="font-semibold text-gray-700">
                                Event Narrative:
                              </span>
                              <p className="mt-1 text-gray-600">
                                {group.parent.EVENT_NARRATIVE || "N/A"}
                              </p>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-700">
                                Episode Narrative:
                              </span>
                              <p className="mt-1 text-gray-600">
                                {group.parent.EPISODE_NARRATIVE || "N/A"}
                              </p>
                            </div>
                          </div>
                          {otherEvents.length > 0 && (
                            <div>
                              <h4 className="text-lg font-semibold text-gray-800 border-b border-gray-300 pb-1">
                                Same Day and Type:
                              </h4>
                              <ul className="space-y-4 mt-4">
                                {otherEvents.map((ev, idx) => (
                                  <li
                                    key={idx}
                                    className="p-4 bg-white border border-gray-200 rounded shadow-sm"
                                  >
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <span className="font-semibold text-gray-700">
                                          Type:
                                        </span>
                                        <span className="ml-1 text-gray-600">
                                          {ev.EVENT_TYPE}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="font-semibold text-gray-700">
                                          CZ:
                                        </span>
                                        <span className="ml-1 text-gray-600">
                                          {ev.CZ_NAME}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="font-semibold text-gray-700">
                                          State:
                                        </span>
                                        <span className="ml-1 text-gray-600">
                                          {ev.STATE}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="font-semibold text-gray-700">
                                          Date:
                                        </span>
                                        <span className="ml-1 text-gray-600">
                                          {ev.date.split("T")[0]}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="font-semibold text-gray-700">
                                          Distance:
                                        </span>
                                        <span className="ml-1 text-gray-600">
                                          {typeof ev.distance === "number"
                                            ? ev.distance.toFixed(2)
                                            : ev.distance}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="mt-2">
                                      <span className="font-semibold text-gray-700">
                                        Event Narrative:
                                      </span>
                                      <p className="mt-1 text-gray-600">
                                        {ev.EVENT_NARRATIVE || "N/A"}
                                      </p>
                                    </div>
                                    <div className="mt-2">
                                      <span className="font-semibold text-gray-700">
                                        Episode Narrative:
                                      </span>
                                      <p className="mt-1 text-gray-600">
                                        {ev.EPISODE_NARRATIVE || "N/A"}
                                      </p>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EventTable;